import { useEffect, useRef } from "react";
import { useField, useFormikContext } from "formik";
import { Card, Flex } from "@jsq/ds/core";
import { FormNumberInput, FormRadioButton, FormRadioGroup, FormSelect } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { CUSTOM_RANGE_KIND_OPTIONS, PERIOD_KIND_OPTIONS } from "./constants";
import { CustomRangeBody } from "./CustomRangeBody";
import { CustomSingleBody } from "./CustomSingleBody";
import { DateTypeRadio } from "./DateTypeRadio";
import { joinPath } from "./paths";
import { periodSelectHelpText } from "./periodHelpText";
import {
  arePeriodValuesEqual,
  normalizePeriod,
  reconcileForCustomRangeKind,
  reconcileForPeriodKind,
} from "./reconcile";
import * as styles from "./styles_vanilla.css";
import { TrailingBody } from "./TrailingBody";
import type { DatePeriodValues } from "./types";
import type { DatePeriodKind, DateRangeKind } from "~/graphql/supergraphTypes";

// TODO(RPRT-3081): mount with `valuesPath="datePeriod"` from
// FieldSettingsPanel; only render for non-primary numeric fields.

export type DatePeriodSectionProps = {
  /** Formik path prefix for the date-period sub-tree (e.g. `"datePeriod"`).
   * Sub-tree shape mirrors `DatePeriodInput` so it round-trips through
   * `toDatePeriodInput` without a boundary mapping. */
  valuesPath: string;
  isReadOnly?: boolean;
  /** When `"inline"`, renders only the date controls (no outer Card). */
  variant?: "card" | "inline";
};

const renderPeriodKindOptions = () => (
  <FormSelect.Section>
    {PERIOD_KIND_OPTIONS.map((option) => (
      <FormSelect.Option key={option.value} value={option.value}>
        {option.label}
      </FormSelect.Option>
    ))}
  </FormSelect.Section>
);

const VariantBody = ({
  periodKind,
  customRangeKind,
  valuesPath,
  isReadOnly,
}: {
  periodKind: DatePeriodKind;
  customRangeKind: DateRangeKind | null;
  valuesPath: string;
  isReadOnly: boolean;
}) => {
  if (periodKind === "TRAILING_MONTHS") {
    return <TrailingBody valuesPath={valuesPath} isReadOnly={isReadOnly} />;
  }
  if (periodKind === "CUSTOM") {
    return customRangeKind === "RANGE" ? (
      <CustomRangeBody valuesPath={valuesPath} isReadOnly={isReadOnly} />
    ) : (
      <CustomSingleBody valuesPath={valuesPath} isReadOnly={isReadOnly} />
    );
  }
  return null;
};

const PeriodSelect = ({
  valuesPath,
  isReadOnly,
  onChange,
}: {
  valuesPath: string;
  isReadOnly: boolean;
  onChange: (next: DatePeriodKind) => void;
}) => {
  const [{ value: periodKind }] = useField<DatePeriodKind>(joinPath(valuesPath, "periodKind"));
  return (
    <FormSelect
      name={joinPath(valuesPath, "periodKind")}
      label="Date period"
      helpText={periodSelectHelpText(periodKind)}
      className={styles.formControl}
      data-testid="date-period-kind-select"
      disabled={isReadOnly}
      handleChange={(raw) => onChange(raw as DatePeriodKind)}
    >
      {renderPeriodKindOptions()}
    </FormSelect>
  );
};

const RangeKindRadio = ({
  valuesPath,
  isReadOnly,
  onChange,
}: {
  valuesPath: string;
  isReadOnly: boolean;
  onChange: (next: DateRangeKind) => void;
}) => (
  <FormRadioGroup
    name={joinPath(valuesPath, "customRangeKind")}
    label="Select date range"
    data-testid="date-period-custom-range-kind"
    onValueChanged={(value) => onChange(value as DateRangeKind)}
  >
    {CUSTOM_RANGE_KIND_OPTIONS.map((option) => (
      <FormRadioButton key={option.value} value={option.value} disabled={isReadOnly}>
        {option.label}
      </FormRadioButton>
    ))}
  </FormRadioGroup>
);

/** Reusable "Date period" card. Reads/writes a `DatePeriodValues` sub-tree at
 * `${valuesPath}` whose shape matches the BE `DatePeriodInput`. */
export const DatePeriodSection = ({
  valuesPath,
  isReadOnly = false,
  variant = "card",
}: DatePeriodSectionProps) => {
  const { setFieldValue } = useFormikContext();
  const [{ value: rootValues }] = useField<DatePeriodValues>(valuesPath);

  // Defend against parents (or persisted drafts) that mount the form already
  // violating the BE per-kind invariants. Run once: subsequent edits flow
  // through the explicit handle*Change reconcilers below.
  const hasNormalizedRef = useRef(false);
  useEffect(() => {
    if (hasNormalizedRef.current) return;
    hasNormalizedRef.current = true;
    const normalized = normalizePeriod(rootValues);
    if (!arePeriodValuesEqual(normalized, rootValues)) {
      void setFieldValue(valuesPath, normalized, false);
    }
  }, [rootValues, setFieldValue, valuesPath]);

  const handlePeriodKindChange = (next: DatePeriodKind) => {
    if (next === rootValues.periodKind) return;
    void setFieldValue(valuesPath, reconcileForPeriodKind(rootValues, next), false);
  };

  const handleCustomRangeKindChange = (next: DateRangeKind) => {
    if (next === rootValues.customRangeKind) return;
    void setFieldValue(valuesPath, reconcileForCustomRangeKind(rootValues, next), false);
  };

  const body = (
    <Flex flexDirection="column" gap={SPACING.LARGE}>
      <DateTypeRadio name={joinPath(valuesPath, "dateType")} isReadOnly={isReadOnly} />
      <PeriodSelect
        valuesPath={valuesPath}
        isReadOnly={isReadOnly}
        onChange={handlePeriodKindChange}
      />
      {rootValues.periodKind === "CUSTOM" && (
        <RangeKindRadio
          valuesPath={valuesPath}
          isReadOnly={isReadOnly}
          onChange={handleCustomRangeKindChange}
        />
      )}
      <VariantBody
        periodKind={rootValues.periodKind}
        customRangeKind={rootValues.customRangeKind}
        valuesPath={valuesPath}
        isReadOnly={isReadOnly}
      />
      <FormNumberInput
        name={joinPath(valuesPath, "offsetMonths")}
        label="Offset by months"
        className={styles.formControl}
        data-testid="date-period-offset-months"
        disabled={isReadOnly}
      />
    </Flex>
  );

  if (variant === "inline") return body;

  return (
    <Card isFullWidth title="Date period">
      {body}
    </Card>
  );
};
